import {
    FunctionComponent,
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';
import {
    DiscussionsContainer,
    DiscussionsIcon,
    DiscussionsNotification,
    LogoContainer,
    ProjectsMenu,
    RightControls,
    StyledAppBar,
    StyledToolbar,
    ToolbarContainer,
    NavbarIconContainer,
    FeedbackIcon,
    BugIcon
} from './styled';
import { ContentWrapper } from '../../pages/styled';
import Box from '@mui/material/Box';
import { ReactComponent as AppLogo } from './../../assets/icons/app-logo.svg';
import { ReactComponent as PersonIcon } from './../../assets/icons/person.svg';
import { ReactComponent as DelegateIcon } from './../../assets/icons/delegate.svg';
import { ReactComponent as AdminBoardIcon } from './../../assets/icons/admin_board.svg';
import { ReactComponent as MainBoardIcon } from './../../assets/icons/main_board.svg';
import { ReactComponent as CommunityIcon } from './../../assets/icons/community.svg';
import { ReactComponent as HelpNavbarIcon } from './../../assets/icons/help_navbar.svg';
import { ReactComponent as EditAccount } from './../../assets/icons/edit-account.svg';
import { ReactComponent as SignoutIcon } from './../../assets/icons/signout.svg';
import { ReactComponent as BeeIcon } from './../../assets/icons/bee.svg';
import { Link } from 'react-router-dom';
import DropdownWithIcon from './components/DropdownWithIcon';
import theme from '../../theme';
import { useNavigate } from 'react-router-dom';
import { signOut, isAdmin, signedIn } from '../../redux/auth/service';
import { RoutesEnum } from '../../types/navigation';
import DiscussionsModal from '../../pages/Chat/components/DiscussionsModal';
import ConversationModal from 'src/pages/Chat/components/ConversationModal';
import { ChatSelectors, useConversations } from '../../redux/chat/selectors';
import useAppDispatch from '../../hooks/useAppDispatch';
import { ClientSelectors } from '../../redux/client/selectors';
import {
    fetchUserConversationState,
    fetchUserPermissions
} from '../../redux/chat/service';
import {
    CONVERSATION_PAGE_SIZE,
    FETCH_CONVERSATION_INTERVAL
} from '../../constants/chat';
import useIntervalCallback from '../../pages/Chat/hooks/useIntervalCallback';
import { generateEntityId, parseEntityId } from '../../utils/chat';
import { ConversationType } from '../../enums/chat';
import {
    closeChat,
    openChatAtMessage,
    setActiveConversation
} from '../../redux/chat/slice';
import { humanize } from '../../utils/string';
import { getContributorsDashboardUrl } from '../../utils/envsConsts';
import FeedbackBugModal from './components/FeedbackBugModal';
import {
    sendGlobalFeedback,
    sendBugReport
} from '../../redux/feedback/service';
import { setSnackbarAttributes } from '../../redux/snackbar/slice';
import { FeedbackType } from 'src/types/general';

export type Props = {
    children: React.ReactNode;
};

interface ModalState {
    open: boolean;
    type: FeedbackType;
}

const Navbar: FunctionComponent<Props> = ({ children }) => {
    const admin = isAdmin();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [discussionsModalOpen, setDiscussionsModalOpen] = useState(false);
    const [modalState, setModalState] = useState<ModalState>({
        open: false,
        type: FeedbackType.FEEDBACK
    });
    const conversations = useConversations();
    const { selectedProjectId } = ClientSelectors();
    const chatState = ChatSelectors();
    const {
        isOpen: isChatOpen,
        activeConversationId,
        entityId: activeConversationEntityId,
        conversationName: activeConversationName
    } = chatState;
    const hasUnreadConversations = useMemo(() => {
        return Object.values(conversations).some(
            (conversation) => conversation.unreadMessageCount > 0
        );
    }, [conversations]);

    const activeConversation =
        activeConversationId !== null
            ? conversations[activeConversationId]
            : undefined;

    const activeEntityId =
        activeConversation?.entityId ?? activeConversationEntityId ?? '';
    const activeConversationTitle =
        humanize(activeConversation?.conversationName || '') ??
        humanize(activeConversationName) ??
        'Conversation';
    const activeConversationSubtitle =
        humanize(activeConversation?.projectName || '') ??
        humanize(activeConversation?.entityId || '') ??
        null;
    const parsedEntity = parseEntityId(activeEntityId);
    const activeConversationType =
        parsedEntity.conversationType ?? ConversationType.PROJECT;
    const isChatModalVisible =
        Boolean(activeConversationId) && Boolean(isChatOpen);

    const pollUserPermissions = useCallback(() => {
        if (!signedIn()) {
            return Promise.resolve(undefined);
        }
        return dispatch(fetchUserPermissions())
            .unwrap()
            .catch(() => undefined);
    }, [dispatch]);

    useEffect(() => {
        pollUserPermissions();
    }, [pollUserPermissions]);

    useEffect(() => {
        if (!signedIn) {
            return;
        }
        const params = new URLSearchParams(window.location.search);
        const conversationId = params.get('conversationId');
        const entityId = params.get('entityId');
        const messageId = params.get('messageId');

        if (conversationId && entityId && messageId) {
            const id = Number(conversationId);
            const msgId = Number(messageId);
            dispatch(
                openChatAtMessage({
                    conversationId: id,
                    entityId: decodeURIComponent(entityId),
                    messageId: msgId,
                    resultsPerPage: CONVERSATION_PAGE_SIZE
                })
            );
            params.delete('conversationId');
            params.delete('entityId');
            params.delete('messageId');
            const newSearch = params.toString();
            window.history.replaceState(
                {},
                '',
                newSearch ? `?${newSearch}` : window.location.pathname
            );
        }
    }, [dispatch]);

    useIntervalCallback(pollUserPermissions, FETCH_CONVERSATION_INTERVAL);

    const ensureProjectConversation = useCallback(async () => {
        if (!selectedProjectId) {
            return;
        }
        const projectEntityId = generateEntityId(
            ConversationType.PROJECT,
            selectedProjectId
        );

        // First check local state
        const projectConversation = Object.values(conversations).find(
            (conversation) => conversation.entityId === projectEntityId
        );

        const fetchAndActivate = async (
            conversationId: number,
            entityId: string
        ) => {
            try {
                await dispatch(
                    fetchUserConversationState({
                        entityId,
                        resultsPerPage: CONVERSATION_PAGE_SIZE
                    })
                ).unwrap();
            } catch (error) {
                // ignore state fetch errors
                console.error('Error fetching conversation state:', error);
            }

            dispatch(setActiveConversation(conversationId));
        };

        // If we have a conversation in local state with a valid ID, use it
        if (
            projectConversation?.conversationId &&
            projectConversation.entityId
        ) {
            await fetchAndActivate(
                projectConversation.conversationId,
                projectConversation.entityId
            );
            return;
        }

        // Otherwise, check with backend for existing conversation
        try {
            const stateResponse = await dispatch(
                fetchUserConversationState({
                    entityId: projectEntityId,
                    resultsPerPage: CONVERSATION_PAGE_SIZE
                })
            ).unwrap();

            // If backend returns a conversationId, use it
            if (stateResponse.data.conversationId) {
                await fetchAndActivate(
                    stateResponse.data.conversationId,
                    projectEntityId
                );
            }
        } catch (error) {
            // If fetchUserConversationState fails, no conversation exists
            // Do not create a new conversation - just return
            console.error('Error fetching project conversation state:', error);
            return;
        }
    }, [conversations, dispatch, selectedProjectId]);

    const handleSignout = () => {
        signOut().then(() => {
            navigate('/login');
        });
    };

    const handleCloseDiscussions = useCallback(() => {
        setDiscussionsModalOpen(false);
    }, []);

    const handleCloseChatModal = useCallback(() => {
        dispatch(closeChat());
    }, [dispatch]);

    const handleOpenDiscussions = useCallback(() => {
        setDiscussionsModalOpen(true);
        pollUserPermissions()
            .catch(() => undefined)
            .finally(async () => {
                await ensureProjectConversation();
                dispatch(closeChat());
            });
    }, [dispatch, ensureProjectConversation, pollUserPermissions]);

    const handleOpenFeedback = useCallback(() => {
        setModalState({ open: true, type: FeedbackType.FEEDBACK });
    }, []);

    const handleOpenBug = useCallback(() => {
        setModalState({ open: true, type: FeedbackType.BUG });
    }, []);

    const handleCloseModal = useCallback(() => {
        setModalState((prev) => ({ ...prev, open: false }));
    }, []);

    const handleSubmitFeedback = useCallback(
        async (description: string, attachmentIds: number[]) => {
            const action =
                modalState.type === FeedbackType.FEEDBACK
                    ? sendGlobalFeedback
                    : sendBugReport;
            const successMessage =
                modalState.type === FeedbackType.FEEDBACK
                    ? 'Feedback submitted successfully!'
                    : 'Bug report submitted successfully!';
            const errorMessage =
                modalState.type === FeedbackType.FEEDBACK
                    ? 'An error occurred while submitting your feedback. Please try again.'
                    : 'An error occurred while submitting your bug report. Please try again.';

            try {
                await dispatch(
                    action({
                        details: description,
                        attachmentList: attachmentIds
                    })
                ).unwrap();

                dispatch(
                    setSnackbarAttributes({
                        snackbarOpen: true,
                        snackbarType: 'success',
                        snackbarMessage: successMessage
                    })
                );
                handleCloseModal();
            } catch (error: any) {
                console.error(`Error submitting ${modalState.type}:`, error);
                dispatch(
                    setSnackbarAttributes({
                        snackbarOpen: true,
                        snackbarType: 'error',
                        snackbarMessage: errorMessage
                    })
                );
                throw error;
            }
        },
        [dispatch, modalState.type, handleCloseModal]
    );

    const menuItemsList = [
        {
            ItemIcon: <DelegateIcon />,
            option: 'Delegate',
            onClick: () => {
                navigate(RoutesEnum.PLAN_QUEST);
            },
            path: RoutesEnum.PLAN_QUEST
        },
        {
            ItemIcon: <MainBoardIcon />,
            option: 'Main board',
            onClick: () => {
                navigate('/overview');
            },
            path: RoutesEnum.CLIENT_DASHBOARD
        },
        ...(admin
            ? [
                  {
                      ItemIcon: <AdminBoardIcon />,
                      option: 'Admin board',
                      onClick: () => {
                          navigate('/projects');
                      },
                      path: RoutesEnum.PROJECT_DASHBOARD
                  },
                  {
                      ItemIcon: <CommunityIcon />,
                      option: 'Community',
                      onClick: () => {
                          navigate('/community');
                      },
                      path: RoutesEnum.COMMUNITY
                  },
                  {
                      ItemIcon: <BeeIcon />,
                      option: 'Contributors dashboard',
                      onClick: () => {
                          window.open(getContributorsDashboardUrl(), '_blank');
                      },
                      path: ''
                  }
              ]
            : []),
        {
            ItemIcon: <EditAccount />,
            option: 'Edit account',
            onClick: () => {
                navigate(RoutesEnum.EDIT_ACCOUNT);
            },
            path: RoutesEnum.EDIT_ACCOUNT
        },
        {
            ItemIcon: <HelpNavbarIcon />,
            option: 'Help',
            onClick: () => {
                window.open('https://docs.caas.ai/community', '_blank');
            },
            path: 'https://docs.caas.ai/community'
        },
        {
            ItemIcon: <SignoutIcon />,
            option: 'Sign out',
            onClick: handleSignout,
            path: '/login'
        }
    ];

    const getModalProps = () => {
        if (modalState.type === FeedbackType.FEEDBACK) {
            return {
                title: 'Feedback',
                description:
                    'Your feedback helps us improve Beehive. Please let us know if you have any suggestions or comments.'
            };
        }
        return {
            title: 'Report Bug',
            description:
                'Found a bug? Please describe the issue and attach screenshots or videos to help us resolve it.'
        };
    };

    return (
        <ToolbarContainer>
            <DiscussionsModal
                open={discussionsModalOpen}
                onClose={handleCloseDiscussions}
            />
            <FeedbackBugModal
                open={modalState.open}
                onClose={handleCloseModal}
                {...getModalProps()}
                onSubmit={handleSubmitFeedback}
            />
            <ConversationModal
                open={isChatModalVisible}
                onClose={handleCloseChatModal}
                conversationId={activeConversationId ?? 0}
                conversationType={activeConversationType}
                entityId={activeEntityId}
                title={activeConversationTitle}
                subtitle={activeConversationSubtitle ?? undefined}
            />
            <StyledAppBar>
                <StyledToolbar>
                    <LogoContainer>
                        <Link to={`${window.location.origin}/overview`}>
                            <AppLogo />
                        </Link>
                    </LogoContainer>
                    <ProjectsMenu />
                    <RightControls>
                        <DiscussionsContainer onClick={handleOpenDiscussions}>
                            <DiscussionsIcon />
                            {hasUnreadConversations && (
                                <DiscussionsNotification />
                            )}
                        </DiscussionsContainer>
                        <NavbarIconContainer onClick={handleOpenBug}>
                            <BugIcon />
                        </NavbarIconContainer>
                        <NavbarIconContainer onClick={handleOpenFeedback}>
                            <FeedbackIcon />
                        </NavbarIconContainer>
                        <DropdownWithIcon
                            icon={<PersonIcon />}
                            list={menuItemsList}
                            title="Settings"
                        />
                    </RightControls>
                </StyledToolbar>
            </StyledAppBar>
            <Box
                component="main"
                sx={{
                    minWidth: '100%',
                    minHeight: '100vh',
                    backgroundColor: theme.color.codGray,
                    position: 'relative'
                }}
            >
                <ContentWrapper dimmed={false}>{children}</ContentWrapper>
            </Box>
        </ToolbarContainer>
    );
};

export default Navbar;
